import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  Text,
  useColorModeValue,
  Button,
  Flex,
  Icon,
  SimpleGrid,
  useToast,
} from "@chakra-ui/react";
import { MdArrowBack, MdShare } from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import useAxios from "axios-hooks";
import { useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "components/feedback/LoadingSpinner";

const SessionSummary = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const toast = useToast();

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const labelColor = useColorModeValue("gray.600", "gray.400");

  const [{ data: sessionResponse, loading: sessionLoading }, getSession] =
    useAxios(
      { method: "GET", url: `/api/chargingSession/${sessionId}` },
      { manual: true }
    );

  const fetchSessionData = async () => {
    try {
      const response = await getSession();
      const session = response?.data?.data;
      setSessionData(session);

      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    } catch (error) {
      console.error("Error fetching session data:", error);
      // ERR code is cancled return
      if (error?.code === "ERR_CANCELED") {
        return;
      }
      toast({
        title: "Error",
        description: "Failed to load session details",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchSessionData();
    }
  }, [sessionId]);

  const handleWhatsappShare = () => {
    const mobileNumber = sessionData?.MobileNumber || sessionData?.IdTag;
    
    if (!mobileNumber) {
      toast({
        title: "Error",
        description: "Mobile number not available",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Format the date
    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    // Format the message with session details
    const message = `Charging Session Summary

Bill No: ${sessionData?.Id || "N/A"}
Date: ${formatDate(sessionData?.StartTime)}

Units Consumed: ${sessionData?.UnitsConsumed?.toFixed(2) || "0.00"} kWh
Tariff: Rs.${sessionData?.AgreedPrice?.toFixed(2) || "0.00"} / kWh

Amount: Rs.${sessionData?.TotalCostWithoutTax?.toFixed(2) || "0.00"}
Tax: Rs.${sessionData?.TaxAmount?.toFixed(2) || "0.00"}

Total Payable: Rs.${sessionData?.TotalCostWithTax?.toFixed(2) || "0.00"}

Thank you for charging with us.
Drive safe!`;
    
    // Create WhatsApp URL with country code
    const whatsappUrl = `https://wa.me/91${mobileNumber}?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, "_blank");
  };

  if (sessionLoading && isInitialLoad) {
    return (
      <Box pt={{ base: 40, md: 10 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (!sessionData) {
    return (
      <Box bg={bgColor} pt={{ base: 40, md: 10 }} px={{ base: 2, md: 4 }}>
        <Text>Session not found</Text>
      </Box>
    );
  }

  const InfoBox = ({ label, value, isLarge = false }) => (
    <Box
      p={{ base: 2, md: 3 }}
      bg="rgba(245, 125, 54, 0.05)"
      borderRadius="lg"
      border="1px solid"
      borderColor="rgba(245, 125, 54, 0.2)"
      minH={isLarge ? { base: "100px", md: "120px" } : "auto"}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <VStack spacing={0.5} align="center">
        <Text
          fontSize={{ base: "2xs", md: "xs" }}
          color={labelColor}
          fontWeight="semibold"
          textAlign="center"
        >
          {label}
        </Text>
        <Text
          fontSize={
            isLarge ? { base: "2xl", md: "3xl" } : { base: "xs", md: "sm" }
          }
          fontWeight="bold"
          color={isLarge ? "#FF5C00" : textColor}
          textAlign="center"
        >
          {value}
        </Text>
      </VStack>
    </Box>
  );

  return (
    <Box bg={bgColor} pt={{ base: 40, md: 10 }} px={{ base: 2, md: 4 }} pb={6}>
      <VStack
        spacing={{ base: 3, md: 4 }}
        align="stretch"
        maxW="600px"
        mx="auto"
      >
        {/* Back Button */}
        <Flex justify="flex-start">
          <Button
            leftIcon={<MdArrowBack />}
            bgColor="#f57d36"
            _hover={{ bgColor: "#d44e00" }}
            onClick={() => navigate(-1)}
            color="white"
            size={{ base: "sm", md: "md" }}
            fontSize={{ base: "xs", md: "sm" }}
            borderRadius={8}
          >
            Back
          </Button>
        </Flex>

        {/* Session Details Heading */}
        <Text
          fontSize={{ base: "lg", md: "xl" }}
          fontWeight="bold"
          color={textColor}
        >
          Session Details
        </Text>

        {/* Session Summary Card */}
        <Box
          bg={cardBg}
          borderRadius="xl"
          border="2px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 4 }}
          boxShadow="lg"
        >
          <VStack spacing={{ base: 2, md: 3 }} align="stretch">
            {/* Customer */}
            <Box
              p={{ base: 2, md: 3 }}
              bg="rgba(245, 125, 54, 0.05)"
              borderRadius="lg"
              border="1px solid"
              borderColor="rgba(245, 125, 54, 0.2)"
            >
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="semibold"
                color={labelColor}
                textAlign="center"
              >
                Customer: +91{" "}
                {sessionData?.MobileNumber || sessionData?.IdTag || "N/A"}
              </Text>
            </Box>

            {/* Bill No, Status, Mode, SOC Grid */}
            <SimpleGrid columns={2} spacing={{ base: 2, md: 3 }}>
              {/* Left Column - 3 equal boxes */}
              <VStack spacing={{ base: 2, md: 3 }} flex="1">
                <Box w="full">
                  <InfoBox
                    label="BILL NO"
                    value={`${sessionData?.Id || "N/A"}`}
                  />
                </Box>
                <Box w="full">
                  <InfoBox
                    label="STATUS"
                    value={sessionData?.Status || "N/A"}
                  />
                </Box>
                <Box w="full">
                  <InfoBox
                    label="MODE"
                    value={
                      sessionData?.Mode === "units"
                        ? "UNITS"
                        : sessionData?.Mode === "time"
                        ? "TIME"
                        : sessionData?.Mode === "money"
                        ? "MONEY"
                        : "N/A"
                    }
                  />
                </Box>
              </VStack>

              {/* Right Column - SOC */}
              <InfoBox
                label="% SOC"
                value={`${sessionData?.ConsumerSoc || "0"}%`}
                isLarge={true}
              />
            </SimpleGrid>

            {/* Requested, Delivered, Tariff, Amount Grid */}
            <SimpleGrid columns={2} spacing={{ base: 2, md: 3 }}>
              <InfoBox
                label="REQUESTED"
                value={
                  sessionData?.Mode === "time"
                    ? `${sessionData?.MinutesRequested || "0"} mins`
                    : sessionData?.Mode === "units" ||
                      sessionData?.Mode === "money"
                    ? `${sessionData?.UnitsRequested || "0"} kWh`
                    : "0"
                }
              />
              <InfoBox
                label="INITIAL METER"
                value={sessionData?.InitialMeterValue?.toFixed(2) || "0.00"}
              />
              <InfoBox
                label="DELIVERED"
                value={`${
                  sessionData?.UnitsConsumed?.toFixed(2) || "0.00"
                } kWh`}
              />
              <InfoBox
                label="FINAL METER"
                value={sessionData?.FinalMeterValue?.toFixed(2) || "0.00"}
              />
              <InfoBox
                label="TARIFF"
                value={`₹${sessionData?.AgreedPrice?.toFixed(2) || "0.00"}`}
              />
              <InfoBox
                label="TAX AMOUNT"
                value={`₹${sessionData?.TaxAmount?.toFixed(2) || "0.00"}`}
              />
              <InfoBox
                label="AMOUNT"
                value={`₹${
                  sessionData?.TotalCostWithoutTax?.toFixed(2) || "0.00"
                }`}
              />
              <InfoBox
                label="TOTAL PAYABLE"
                value={`₹${
                  sessionData?.TotalCostWithTax?.toFixed(2) || "0.00"
                }`}
              />
            </SimpleGrid>

            {/* WhatsApp Share Button */}
            <Button
              colorScheme="green"
              size={{ base: "md", md: "lg" }}
              leftIcon={
                <Flex align="center" gap={1}>
                  <Icon as={MdShare} />
                  <Icon as={FaWhatsapp} />
                </Flex>
              }
              onClick={handleWhatsappShare}
              borderRadius="lg"
              fontSize={{ base: "sm", md: "md" }}
              h={{ base: "44px", md: "48px" }}
            >
              Bill
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default SessionSummary;
